import { StateCreator } from "zustand";
import { WasteCatagoryType } from "@utils/isValidWasteCategoryAndRoute";

type WasteSubcategory = {
  name: string;
  parentCategory: string;
};

export type WasteLocation = {
  address_line_1: string;
  address_line_2: string;
  town_city: string;
  county: string;
  postcode: string;
  country: string;
  UPRN: string;
  USRN: string;
  latitude: string;
  longitude: string;
  officialAddress: string;
};

type Question = Array<{
  key: string;
  name: string;
  parentSubcategories: string[];
  fieldType: string;
  response: string;
}>;

export type CaseIDProps = {
  case_reference: string;
  sla_text: string;
};

export const emptyLocation = {
  address_line_1: "",
  address_line_2: "",
  town_city: "",
  county: "",
  postcode: "",
  country: "",
  UPRN: "",
  USRN: "",
  latitude: "",
  longitude: "",
  officialAddress: "",
};

export interface WasteDetailsSlice {
  wasteCategory: WasteCatagoryType;
  isImageMismatch: boolean;
  currentSubcategory: WasteSubcategory | undefined;
  renderedQuestions: Question | undefined;
  locationOfWaste: WasteLocation;
  canYouGiveMoreDetail: string;
  anythingElseWeNeedToKnow: string;
  receiveConfirmationEmail: string;
  surfaceDetails: string;
  actualMeasurementDetails: string;
  offensive: boolean;
  confirmation: CaseIDProps | undefined;
  inferredWasteCategory: string[];
  inferredNumberOfBinBags: string;
  updateWasteCategory: (wasteCategory: WasteCatagoryType) => void;
  updateCurrentSubcategory: (
    currentSubcategory: WasteSubcategory | undefined,
  ) => void;
  updateRenderedQuestions: (renderedQuestions: Question | undefined) => void;
  updateConfirmation: (case_id: CaseIDProps | undefined) => void;
  updateLocationOfWaste: (locationOfWaste: WasteLocation) => void;
  updateCanYouGiveMoreDetail: (moreDetailOnWaste: string) => void;
  updateAnythingElseWeNeedToKnow: (anythingElseWeNeedToKnow: string) => void;
  updateReceiveConfirmationEmail: (receiveConfirmationEmail: string) => void;
  updateSurfacedetails: (surfaceDetails: string) => void;
  updateActualMeasurementDetails: (actualMeasurementDetails: string) => void;
  updateOffensive: (offensive: boolean) => void;
  updateIsImageMismatch: (isImageMismatch: boolean) => void;
  resetWasteDetails: () => void;
  updateInferredWasteCategory: (inferredWasteCategory: string[]) => void;
  updateInferredNumberOfBinBags: (inferredNumberOfBinBags: string) => void;
  resetAllWasteDetails: () => void;
}

export const createWasteDetailsSlice: StateCreator<
  WasteDetailsSlice,
  [],
  [],
  WasteDetailsSlice
> = (set) => ({
  wasteCategory: "",
  currentSubcategory: {
    name: "",
    parentCategory: "",
  },
  locationOfWaste: emptyLocation,
  canYouGiveMoreDetail: "",
  anythingElseWeNeedToKnow: "",
  receiveConfirmationEmail: "",
  isImageMismatch: false,
  renderedQuestions: undefined,
  surfaceDetails: "",
  actualMeasurementDetails: "",
  offensive: false,
  confirmation: undefined,
  inferredWasteCategory: [],
  inferredNumberOfBinBags: "",
  updateWasteCategory: (wasteCategory) => set(() => ({ wasteCategory })),
  updateCurrentSubcategory: (
    currentSubcategory: WasteSubcategory | undefined,
  ) => set(() => ({ currentSubcategory })),
  updateRenderedQuestions: (renderedQuestions: Question | undefined) =>
    set(() => ({ renderedQuestions })),
  updateConfirmation: (confirmation: CaseIDProps | undefined) =>
    set(() => ({ confirmation })),
  updateLocationOfWaste: (locationOfWaste) => set(() => ({ locationOfWaste })),
  updateCanYouGiveMoreDetail: (canYouGiveMoreDetail) =>
    set(() => ({ canYouGiveMoreDetail })),
  updateAnythingElseWeNeedToKnow: (anythingElseWeNeedToKnow) =>
    set(() => ({ anythingElseWeNeedToKnow })),
  updateReceiveConfirmationEmail: (receiveConfirmationEmail) =>
    set(() => ({ receiveConfirmationEmail })),
  updateSurfacedetails: (surfaceDetails) => set(() => ({ surfaceDetails })),
  updateOffensive: (offensive) => set(() => ({ offensive })),
  updateActualMeasurementDetails: (actualMeasurementDetails) =>
    set(() => ({ actualMeasurementDetails })),
  updateIsImageMismatch: (isImageMismatch) => set(() => ({ isImageMismatch })),
  updateInferredNumberOfBinBags: (inferredNumberOfBinBags) => set(() => ({ inferredNumberOfBinBags })),
  resetWasteDetails: () =>
    set(() => ({
      currentSubcategory: {
        name: "",
        parentCategory: "",
      },
      canYouGiveMoreDetail: "",
      locationOfWaste: emptyLocation,
      anythingElseWeNeedToKnow: "",
      receiveConfirmationEmail: "",
      renderedQuestions: undefined,
      surfaceDetails: "",
      actualMeasurementDetails: "",
      offensive: false,
    })),
  resetAllWasteDetails: () =>
    set(() => ({
      currentSubcategory: {
        name: "",
        parentCategory: "",
      },
      locationOfWaste: emptyLocation,
      canYouGiveMoreDetail: "",
      anythingElseWeNeedToKnow: "",
      receiveConfirmationEmail: "",
      isImageMismatch: false,
      renderedQuestions: undefined,
      surfaceDetails: "",
      actualMeasurementDetails: "",
      offensive: false,
    })),
  updateInferredWasteCategory: (inferredWasteCategory) => set(() => ({ inferredWasteCategory })),
});
